import { useEffect, useState } from "react";
import { crDateTime } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Eye, MousePointerClick, AlertTriangle, Ban, CheckCircle2, Clock } from "lucide-react";

const STATUS_META = {
  sent:        { icon: Clock,            color: "text-slate-500",  bg: "bg-slate-100 text-slate-700" },
  delivered:   { icon: CheckCircle2,     color: "text-blue-600",   bg: "bg-blue-100 text-blue-700" },
  opened:      { icon: Eye,              color: "text-emerald-600",bg: "bg-emerald-100 text-emerald-700" },
  clicked:     { icon: MousePointerClick,color: "text-violet-600", bg: "bg-violet-100 text-violet-700" },
  bounced:     { icon: AlertTriangle,    color: "text-rose-600",   bg: "bg-rose-100 text-rose-700" },
  complained:  { icon: Ban,              color: "text-orange-600", bg: "bg-orange-100 text-orange-700" },
  failed:      { icon: AlertTriangle,    color: "text-rose-600",   bg: "bg-rose-100 text-rose-700" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return crDateTime(iso) || iso; } catch { return iso; }
}

/**
 * Reusable modal that displays per-recipient delivery status for any campaign.
 * Set `open` + provide `campaignType` and `campaignId`.
 */
export default function EmailStatsModal({ open, onOpenChange, campaignType, campaignId, title }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaignType || !campaignId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/email-events?campaign_type=${campaignType}&campaign_id=${campaignId}`);
        if (!cancel) {
          setItems(data.items || []);
          setStats(data.stats || null);
        }
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open, campaignType, campaignId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-stats-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-brand-blue" />
            {title || t("email_stats_title")}
          </DialogTitle>
          <DialogDescription>{t("email_stats_desc")}</DialogDescription>
        </DialogHeader>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-3">
            <StatCard label={t("email_stats_total")}     value={stats.total}     color="bg-slate-50 border-slate-200" data-testid="stat-total" />
            <StatCard label={t("email_stats_delivered")} value={stats.delivered} color="bg-blue-50 border-blue-200"   data-testid="stat-delivered" />
            <StatCard label={t("email_stats_opened")}    value={stats.opened}    color="bg-emerald-50 border-emerald-200" data-testid="stat-opened" />
            <StatCard label={t("email_stats_bounced")}   value={stats.bounced + (stats.complained || 0)}   color="bg-rose-50 border-rose-200"   data-testid="stat-bounced" />
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">{t("email_stats_empty")}</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-2">{t("email_stats_recipient")}</th>
                  <th className="text-left p-2">{t("email_stats_status")}</th>
                  <th className="text-left p-2 hidden sm:table-cell">{t("email_stats_sent_at")}</th>
                  <th className="text-left p-2 hidden md:table-cell">{t("email_stats_details")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.sent;
                  const Icon = meta.icon;
                  return (
                    <tr key={it.id} className="border-t" data-testid={`email-row-${it.email_id}`}>
                      <td className="p-2 text-slate-800 break-all">{it.to}</td>
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${meta.bg}`}>
                          <Icon className="h-3 w-3" />
                          {t(`email_status_${it.status}`)}
                          {it.open_count > 1 && <span className="opacity-70">×{it.open_count}</span>}
                        </span>
                      </td>
                      <td className="p-2 text-xs text-slate-500 hidden sm:table-cell">{fmt(it.sent_at)}</td>
                      <td className="p-2 text-xs text-slate-500 hidden md:table-cell">
                        {it.status === "opened" && it.last_opened_at ? `Abierto: ${fmt(it.last_opened_at)}` :
                         it.status === "bounced" ? <span className="text-rose-600 font-medium">{it.bounce_reason || t("email_bounce_generic")}</span> :
                         it.status === "clicked" && it.clicked_at ? `Click: ${fmt(it.clicked_at)}` :
                         it.status === "delivered" && it.delivered_at ? `Entregado: ${fmt(it.delivered_at)}` :
                         "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, color, ...rest }) {
  return (
    <div className={`rounded-md border p-3 ${color}`} {...rest}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value ?? 0}</div>
    </div>
  );
}
